import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { createLead, linkUser } from "./adminLeadService";
import { useToast } from "../../common/toast/ToastContext";
import PhoneInput from "../../common/components/PhoneInput";
import "../courses/adminCourses.css";

const LEAD_SOURCES = [
  "ORGANIC", "REFERRAL", "WHATSAPP", "INSTAGRAM", "FACEBOOK",
  "SOCIAL_MEDIA", "PAID_AD", "WALK_IN", "EMAIL", "WEBSITE", "OTHER",
];

const LEAD_SOURCE_LABELS = {
  ORGANIC:      "Organic",
  REFERRAL:     "Referral",
  WHATSAPP:     "WhatsApp",
  INSTAGRAM:    "Instagram",
  FACEBOOK:     "Facebook",
  SOCIAL_MEDIA: "Social Media",
  PAID_AD:      "Paid Ad",
  WALK_IN:      "Walk In",
  EMAIL:        "Email",
  WEBSITE:      "Website",
  OTHER:        "Other",
};

const ENGLISH_LEVELS = ["Beginner", "Elementary", "Pre-Intermediate", "Intermediate", "Upper-Intermediate", "Advanced"];

const LEAD_STATUSES = [
  "NEW", "CONTACTED", "INTERESTED", "DEMO_SCHEDULED", "DEMO_DONE",
  "CONVERTED", "NOT_INTERESTED", "NURTURING", "ENROLLED", "LOST",
];

const AdminLeadFormPage = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast }  = useToast();

  const prefillUserId = searchParams.get("userId") || "";
  const prefillName   = searchParams.get("name")   || "";
  const prefillEmail  = searchParams.get("email")  || "";
  const prefillMobile = searchParams.get("mobile") || "";

  const [form, setForm] = useState({
    name:           prefillName,
    email:          prefillEmail,
    whatsappNumber: prefillMobile,
    countryCode:    "+91",             // UI only — not sent to backend
    leadSource:     "ORGANIC",
    currentStatus:  "NEW",
    englishLevel:   "",
    learningGoal:   "",
    notes:          "",
    enquiryDate:    new Date().toISOString().slice(0, 10),
  });
  const [countryCodes, setCountryCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then((r) => setCountryCodes(r.data?.data || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Name is required."); return; }
    setError(""); setSaving(true);
    try {
      // Send only the fields the backend expects — exclude UI-only countryCode
      const { countryCode, ...payload } = form;
      const res = await createLead(payload);
      const newLeadId = res.data?.id;
      if (prefillUserId && newLeadId) {
        await linkUser(newLeadId, Number(prefillUserId));
      }
      showToast("Lead created successfully.", "success");
      navigate(`/admin/leads/${newLeadId}`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="ac-header">
        <h2>Create Lead</h2>
        <button className="ac-btn ac-btn-ghost" onClick={() => navigate("/admin/leads")}>
          ← Back
        </button>
      </div>

      {prefillUserId && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "0.6rem 1rem", marginBottom: 16, fontSize: "0.88rem", color: "#1d4ed8" }}>
          Pre-filled from registered user (ID: {prefillUserId}). Lead will be linked automatically on save.
        </div>
      )}

      <div className="ac-form-card">
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: "0.85rem" }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>Name *</label>
              <input className="ac-input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
            <div className="ac-form-group">
              <label>Email</label>
              <input className="ac-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>

          <div className="ac-form-group">
            <label>WhatsApp Number</label>
            <PhoneInput
              countryCodes={countryCodes}
              dialCode={form.countryCode}
              onDialCodeChange={(val) => set("countryCode", val)}
              mobileNumber={form.whatsappNumber}
              onMobileNumberChange={(val) => set("whatsappNumber", val)}
              placeholder="WhatsApp Number"
            />
          </div>

          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>Lead Source</label>
              <select className="ac-input" value={form.leadSource} onChange={(e) => set("leadSource", e.target.value)}>
                {LEAD_SOURCES.map((s) => (
                  <option key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="ac-form-group">
              <label>Status</label>
              <select className="ac-input" value={form.currentStatus} onChange={(e) => set("currentStatus", e.target.value)}>
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>English Level</label>
              <select className="ac-input" value={form.englishLevel} onChange={(e) => set("englishLevel", e.target.value)}>
                <option value="">— select —</option>
                {ENGLISH_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="ac-form-group">
              <label>Enquiry Date</label>
              <input className="ac-input" type="date" value={form.enquiryDate} onChange={(e) => set("enquiryDate", e.target.value)} />
            </div>
          </div>

          <div className="ac-form-group">
            <label>Learning Goal</label>
            <textarea className="ac-input" value={form.learningGoal} onChange={(e) => set("learningGoal", e.target.value)} placeholder="e.g. Wants to speak fluently for job interviews" rows={3} />
          </div>

          <div className="ac-form-group">
            <label>Notes</label>
            <textarea className="ac-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
          </div>

          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Create Lead"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={() => navigate("/admin/leads")}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLeadFormPage;
